import React from 'react';

// Global keyboard shortcut registry.

type ShortcutHandler = (e: KeyboardEvent) => void;

interface Registration {
    combo: string;
    handler: ShortcutHandler;
}

const registrations = new Set<Registration>();
let listening = false;

function isEditableTarget(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el) {
        return false;
    }
    if (el.dataset.allowShortcuts === 'true') {
        return false;
    }
    if (el.isContentEditable) {
        return true;
    }
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

function eventToCombo(e: KeyboardEvent): string | null {
    const key = e.key;
    if (!key || key === 'Control' || key === 'Alt' || key === 'Shift' || key === 'Meta') {
        return null;
    }
    const parts: string[] = [];
    if (e.ctrlKey || e.metaKey) {
        parts.push('mod');
    }
    if (e.altKey) {
        parts.push('alt');
    }
    if (e.shiftKey) {
        parts.push('shift');
    }
    parts.push(key.toLowerCase());
    return parts.join('+');
}

function onKeyDown(e: KeyboardEvent) {
    const combo = eventToCombo(e);
    if (!combo) {
        return;
    }
    const hasMod = e.ctrlKey || e.metaKey;
    if (isEditableTarget(e.target) && !hasMod) {
        return;
    }
    for (const reg of registrations) {
        if (reg.combo === combo) {
            e.preventDefault();
            reg.handler(e);
            return;
        }
    }
}

export function registerShortcut(combo: string, handler: ShortcutHandler): () => void {
    const registration: Registration = { combo: combo.toLowerCase(), handler };
    registrations.add(registration);
    if (!listening) {
        document.addEventListener('keydown', onKeyDown);
        listening = true;
    }
    return () => {
        registrations.delete(registration);
    };
}

export function useShortcut(combo: string | undefined, handler: ShortcutHandler): void {
    const handlerRef = React.useRef(handler);
    handlerRef.current = handler;

    React.useEffect(() => {
        if (!combo) {
            return;
        }
        return registerShortcut(combo, (e) => handlerRef.current(e));
    }, [combo]);
}
